import {assertPartialSchema, createAction, useSelector, v} from '../../../lib'
import {IIdentityAnnouncement} from '../../../shared/db/identity_announcement'
import {
  toHydratedDate,
  toHydratedEntityModelDates,
  toSerializableDate,
  toSerializableEntityModelDates,
} from '../../serializable'

export enum UnreadAnnouncementActionType {
  UNREAD_ANNOUNCEMENT_LIST_SET = 'UNREAD_ANNOUNCEMENT_LIST_SET',
  UNREAD_ANNOUNCEMENT_DELETE = 'UNREAD_ANNOUNCEMENT_DELETE',
}

export const doUNREAD_ANNOUNCEMENT_LIST_SET = createAction(UnreadAnnouncementActionType.UNREAD_ANNOUNCEMENT_LIST_SET)
export const doUNREAD_ANNOUNCEMENT_DELETE = createAction(UnreadAnnouncementActionType.UNREAD_ANNOUNCEMENT_DELETE)

export const unreadAnnouncementActionCreators = {
  doUNREAD_ANNOUNCEMENT_LIST_SET,
  doUNREAD_ANNOUNCEMENT_DELETE,
}

interface UnreadAnnouncementState {
  announcementList: IIdentityAnnouncement[]
}

interface RootState {
  unreadAnnouncement: UnreadAnnouncementState
}

let cachedUnreadAnnouncementStateRaw: UnreadAnnouncementState | null = null
let cachedUnreadAnnouncementStateHydrated: UnreadAnnouncementState | null = null

export const selectUnreadAnnouncement = () => {
  return useSelector((state: RootState) => hydrateUnreadAnnouncementState(state.unreadAnnouncement ?? null))
}

export const unreadAnnouncementDefaultState: UnreadAnnouncementState = {
  announcementList: [],
}

type Action =
  | {
      type: UnreadAnnouncementActionType.UNREAD_ANNOUNCEMENT_LIST_SET
      payload: IIdentityAnnouncement[]
    }
  | {
      type: UnreadAnnouncementActionType.UNREAD_ANNOUNCEMENT_DELETE
      payload: {
        announcementId: string
      }
    }

export const unreadAnnouncementReducer = (
  state = unreadAnnouncementDefaultState,
  {type, payload}: Action,
): UnreadAnnouncementState => {
  switch (type) {
    case UnreadAnnouncementActionType.UNREAD_ANNOUNCEMENT_LIST_SET:
      assertPartialSchema({
        payload,
        schema: v.array().items(
          v.object<IIdentityAnnouncement>({
            id: v.string().uuid().exist(),
            title: v.string().exist(),
            content: v.string().exist(),
            enabled: v.boolean().exist(),
            mediaUrl: v.string().allow(null).optional(),
            publishDate: v.date().allow(null).optional(),
            link: v.string().allow(null).optional(),
            createdAt: v.date().exist(),
            updatedAt: v.date().exist(),
          }),
        ),
      })
      return {
        ...state,
        announcementList: payload.map((announcement) => normaliseAnnouncement(announcement)),
      }
    case UnreadAnnouncementActionType.UNREAD_ANNOUNCEMENT_DELETE:
      assertPartialSchema({
        payload,
        schema: v
          .object({
            announcementId: v.string().uuid().exist(),
          })
          .exist(),
      })
      return {
        ...state,
        announcementList: state.announcementList.filter((announcement) => announcement.id !== payload.announcementId),
      }
    default:
      return state
  }
}

export const hydrateUnreadAnnouncementState = (
  state: UnreadAnnouncementState | null,
): UnreadAnnouncementState | null => {
  if (!state) {
    return null
  }

  if (cachedUnreadAnnouncementStateRaw === state && cachedUnreadAnnouncementStateHydrated) {
    return cachedUnreadAnnouncementStateHydrated
  }

  const hydratedState = {
    ...state,
    announcementList: state.announcementList.map((announcement) => hydrateAnnouncement(announcement)),
  }

  cachedUnreadAnnouncementStateRaw = state
  cachedUnreadAnnouncementStateHydrated = hydratedState

  return hydratedState
}

function normaliseAnnouncement(announcement: IIdentityAnnouncement): IIdentityAnnouncement {
  return {
    ...toSerializableEntityModelDates(announcement),
    publishDate:
      announcement.publishDate == null ? undefined : (toSerializableDate(announcement.publishDate) as string),
  }
}

function hydrateAnnouncement(announcement: IIdentityAnnouncement): IIdentityAnnouncement {
  return {
    ...toHydratedEntityModelDates(announcement),
    publishDate: announcement.publishDate == null ? undefined : (toHydratedDate(announcement.publishDate) as Date),
  }
}
